import type {
	MaterialGroup,
	Product3dConfig,
	ProductResponse,
} from './product.types';
import {
	MATERIAL_SLOTS_BY_DIMENSION,
	STATIC_MATERIALS,
} from './productModelConfig';

// The Marketing API exposes the configurator's 3D model as a product asset
// keyed by "3D Model" (see /v1/products/{id} response).
const MODEL_3D_ASSET_KEY = '3D Model';

/**
 * Derives the configurator's {@link Product3dConfig} from a product. The model
 * is served from the asset CDN under the "3D Model" key; its `contentUrl` (an
 * extension-less binary URL) is the GLB the viewer loads.
 *
 * The API does not yet return the material configuration, so the dimension ->
 * GLB slot mapping and the fixed materials come from the global convention in
 * {@link MATERIAL_SLOTS_BY_DIMENSION} / {@link STATIC_MATERIALS} — the same for
 * every model. Groups carry no local options: each selection's swatch URL and
 * real-world uv repeat are resolved live from the family/dimension asset map in
 * `buildModelMaterials`, which skips any dimension a product does not have. The
 * viewer likewise ignores any GLB slot a given model lacks, so emitting the full
 * set here is safe and requires no per-model configuration.
 */
export function buildProduct3dConfig(
	product: ProductResponse,
): Product3dConfig | undefined {
	const modelUrl = product.assets?.[MODEL_3D_ASSET_KEY]?.[0]?.contentUrl;
	if (!modelUrl) return undefined;

	return buildProduct3dConfigFromModelUrl(modelUrl);
}

/**
 * Builds the configurator config for a known GLB url, independent of any single
 * product's asset list. Every SKU in a family shares one model (textured live
 * per selection), but not every sibling SKU carries the "3D Model" asset in its
 * own response. The PDP reuses the family's model url here so switching to such
 * a sibling re-textures the shared model rather than dropping the viewer.
 */
export function buildProduct3dConfigFromModelUrl(
	modelUrl: string,
): Product3dConfig {
	const materialGroups: MaterialGroup[] = Object.entries(
		MATERIAL_SLOTS_BY_DIMENSION,
	).map(([dimensionSlug, slots]) => ({
		selectorType: dimensionSlug,
		slots,
		options: [],
		defaultIndex: 0,
	}));

	return {
		modelUrl,
		materialGroups,
		staticMaterials: STATIC_MATERIALS,
	};
}
